import {
  Bell,
  BellRing,
  CalendarCheck,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Home,
  LayoutGrid,
  Megaphone,
  Menu,
  Settings,
  type LucideIcon,
} from "lucide-react";
import { MODULES_REGISTRY } from "@/core/navigation/modulesRegistry";
import { CORES_MODULO } from "@/modules/calendario/cores";
import { useTheme } from "@/shared/theme/ThemeProvider";
import logoLight from "@/shared/assets/elos-wordmark-light.svg";
import logoDark from "@/shared/assets/elos-wordmark-dark.svg";
import {
  ILUSTRACOES_ELO,
  ILUSTRACOES_LIDERANCA,
  type IlustracaoEloId,
  type IlustracaoId,
  type IlustracaoLiderancaId,
  type PapelAjuda,
} from "@/core/ajuda/licoes";
import { IlustracaoElo } from "@/core/components/ajuda/IlustracaoElo";
import { IlustracaoLideranca } from "@/core/components/ajuda/IlustracaoLideranca";
import {
  DESTAQUE,
  Interruptor,
  ItemAviso,
  Moldura,
  Tela,
} from "@/core/components/ajuda/ilustracaoPecas";

/**
 * Ilustrações ESTÁTICAS da Ajuda do Elos. Estas são as das três lições para todos; as das lições de
 * ministério, liderança e Gestor de elo estão em IlustracaoLideranca, e as de cada elo em
 * IlustracaoElo. As peças comuns (moldura, botões, selos) ficam em ilustracaoPecas.
 *
 * São desenhos, não telas de verdade: não têm botão, link nem campo, então
 * nada nelas pode ser tocado ou receber foco, e nenhuma delas lê dado da
 * pessoa (só a lista de elos liberados, passada por props). Usam só os tokens
 * de cor e os ícones lucide do Elos (nada de cor crua), e acompanham o tema
 * claro/escuro. Cada uma tem uma descrição em texto para leitor de tela.
 */

function Botao({
  icone: Icone,
  destacado = false,
  bolinha = false,
}: {
  icone: LucideIcon;
  destacado?: boolean;
  bolinha?: boolean;
}) {
  return (
    <span
      className={`relative grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-border bg-surface text-text ${
        destacado ? DESTAQUE : ""
      }`}
    >
      <Icone size={20} />
      {bolinha && (
        <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-danger ring-2 ring-surface" />
      )}
    </span>
  );
}

function CabecalhoRaiz({ sino = false, menu = false }: { sino?: boolean; menu?: boolean }) {
  const { theme } = useTheme();
  return (
    <div className="flex items-center gap-3 border-b border-border bg-bg px-3 py-2.5">
      <Botao icone={Bell} destacado={sino} bolinha={sino} />
      <div className="min-w-0 flex-1 text-center">
        <img
          src={theme === "dark" ? logoDark : logoLight}
          alt=""
          className="inline-block h-7 w-auto"
        />
      </div>
      <Botao icone={Menu} destacado={menu} />
    </div>
  );
}

function CabecalhoInterno({ titulo, voltar = false }: { titulo: string; voltar?: boolean }) {
  return (
    <div className="flex items-center gap-3 border-b border-border bg-bg px-3 py-2.5">
      <Botao icone={ChevronLeft} destacado={voltar} />
      <div className="min-w-0 flex-1 truncate text-center text-base font-extrabold text-text">
        {titulo}
      </div>
      <Botao icone={Menu} />
    </div>
  );
}

function ConteudoVazio() {
  return <div className="px-3 py-6 text-center text-xs text-muted">… conteúdo da tela …</div>;
}

const ABAS: { rotulo: string; icone: LucideIcon; chave: "inicio" | "calendario" | "central" }[] = [
  { rotulo: "Início", icone: Home, chave: "inicio" },
  { rotulo: "Calendário", icone: CalendarRange, chave: "calendario" },
  { rotulo: "Central Elos", icone: LayoutGrid, chave: "central" },
];

function BarraInferior({
  ativa,
  destacar,
}: {
  ativa: "inicio" | "calendario" | "central";
  destacar: "todas" | "central";
}) {
  return (
    <div className="flex border-t border-border bg-surface">
      {ABAS.map(({ rotulo, icone: Icone, chave }) => {
        const marcada = destacar === "todas" || chave === destacar;
        return (
          <span
            key={chave}
            className={`m-1 flex flex-1 flex-col items-center gap-1 rounded-lg py-2 text-[11px] font-semibold ${
              chave === ativa ? "text-primary" : "text-muted"
            } ${marcada ? "ring-2 ring-primary" : ""}`}
          >
            <Icone size={20} />
            {rotulo}
          </span>
        );
      })}
    </div>
  );
}

function LinhaElo({
  icone: Icone,
  nome,
  compacto = false,
}: {
  icone: LucideIcon;
  nome: string;
  compacto?: boolean;
}) {
  return (
    <li
      className={`flex items-center rounded-xl border border-border bg-surface ${
        compacto ? "gap-2 p-2" : "gap-3 p-3"
      }`}
    >
      <span
        className={`grid shrink-0 place-items-center rounded-lg bg-primary/10 text-primary ${
          compacto ? "h-7 w-7" : "h-9 w-9"
        }`}
      >
        <Icone size={compacto ? 16 : 18} />
      </span>
      <span
        className={`min-w-0 flex-1 break-words font-bold text-text ${
          compacto ? "text-[13px]" : "text-sm"
        }`}
      >
        {nome}
      </span>
      {!compacto && <ChevronRight size={16} className="text-muted" />}
    </li>
  );
}

interface IlustracaoProps {
  id: IlustracaoId;
  /** Elos que a pessoa pode ver (modulosVisiveis), para as ilustrações que listam áreas. */
  modulosVisiveis: string[];
  /** Papel da pessoa, para as ilustrações das lições de elo que mudam conforme ela gerencia o elo. */
  papel: PapelAjuda;
}

const ehDeLideranca = (id: IlustracaoId): id is IlustracaoLiderancaId =>
  (ILUSTRACOES_LIDERANCA as readonly string[]).includes(id);
const ehDeElo = (id: IlustracaoId): id is IlustracaoEloId =>
  (ILUSTRACOES_ELO as readonly string[]).includes(id);

export function Ilustracao({ id, modulosVisiveis, papel }: IlustracaoProps) {
  if (ehDeLideranca(id)) return <IlustracaoLideranca id={id} />;
  if (ehDeElo(id)) return <IlustracaoElo id={id} papel={papel} />;

  const elos = MODULES_REGISTRY.filter((m) => modulosVisiveis.includes(m.code)).slice(0, 3);
  // "Você" x "Outra pessoa": a outra pessoa só mostra elos que você NÃO vê. Se você já vê todos,
  // o desenho inverte: você vê mais do que a outra pessoa. Assim a figura nunca contradiz o texto.
  const naoVisiveis = MODULES_REGISTRY.filter((m) => !modulosVisiveis.includes(m.code)).slice(0, 2);
  const elosVoce = elos.slice(0, 2);
  const elosOutra = naoVisiveis.length ? naoVisiveis : elos.slice(0, 1);

  switch (id) {
    case "abas":
      return (
        <Moldura descricao="Barra de baixo do Elos com três botões: Início, Calendário e Central Elos">
          <Tela>
            <ConteudoVazio />
            <BarraInferior ativa="inicio" destacar="todas" />
          </Tela>
        </Moldura>
      );

    case "central":
      return (
        <Moldura descricao="Lista das áreas liberadas, com a aba Central Elos destacada">
          <Tela>
            <ul className="m-0 flex list-none flex-col gap-2 p-3">
              {elos.length ? (
                elos.map((m) => <LinhaElo key={m.code} icone={m.icon} nome={m.label} />)
              ) : (
                <li className="p-3 text-sm text-muted">Nenhum elo disponível no momento.</li>
              )}
            </ul>
            <BarraInferior ativa="central" destacar="central" />
          </Tela>
        </Moldura>
      );

    case "menu":
      return (
        <Moldura descricao="Topo da tela com o botão de menu destacado, no canto direito">
          <Tela>
            <CabecalhoRaiz menu />
            <ConteudoVazio />
          </Tela>
        </Moldura>
      );

    case "voltar":
      return (
        <Moldura descricao="Topo de uma tela interna com a seta de voltar destacada, no canto esquerdo">
          <Tela>
            <CabecalhoInterno titulo="Escala" voltar />
            <ConteudoVazio />
          </Tela>
        </Moldura>
      );

    case "nao-achou":
      return (
        <Moldura descricao="Duas pessoas veem listas de áreas diferentes">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="mb-2 text-[11px] font-bold uppercase text-muted">Você</p>
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {elosVoce.map((m) => (
                  <LinhaElo key={m.code} icone={m.icon} nome={m.label} compacto />
                ))}
                {!elosVoce.length && (
                  <li className="rounded-xl border border-border bg-surface p-2 text-[13px] text-muted">
                    Nenhuma
                  </li>
                )}
              </ul>
            </div>
            <div>
              <p className="mb-2 text-[11px] font-bold uppercase text-muted">Outra pessoa</p>
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {elosOutra.map((m) => (
                  <LinhaElo key={m.code} icone={m.icon} nome={m.label} compacto />
                ))}
              </ul>
            </div>
          </div>
        </Moldura>
      );

    case "sino":
      return (
        <Moldura descricao="Topo da tela com o sino destacado, no canto esquerdo, com uma bolinha vermelha">
          <Tela>
            <CabecalhoRaiz sino />
            <ConteudoVazio />
          </Tela>
        </Moldura>
      );

    case "painel":
      return (
        <Moldura descricao="Lista de avisos: um novo, com bolinha, e um já lido, mais claro">
          <div className="flex flex-col gap-2">
            <ItemAviso
              icone={CalendarCheck}
              texto="Você foi escalado: Culto de domingo"
              quando="há 2 horas"
            />
            <ItemAviso
              icone={Megaphone}
              texto="Novo comunicado: Retiro de jovens"
              quando="ontem"
              lido
            />
          </div>
        </Moldura>
      );

    case "preferencias":
      return (
        <Moldura descricao="Topo da lista de avisos com a engrenagem Configurar notificações destacada, e duas opções com chave de liga e desliga">
          <Tela>
            <div className="flex items-center justify-between gap-3 border-b border-border bg-surface px-3 py-2.5">
              <span className="text-sm font-bold text-text">Notificações</span>
              <span className={`grid h-8 w-8 place-items-center rounded-lg text-muted ${DESTAQUE}`}>
                <Settings size={16} />
              </span>
            </div>
            <div className="flex flex-col gap-3 p-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-text">Escalas</span>
                <Interruptor ligado />
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-text">Avisos neste aparelho</span>
                <Interruptor ligado={false} />
              </div>
            </div>
          </Tela>
        </Moldura>
      );

    case "aviso-comunicado":
      return (
        <Moldura descricao="Dois cartões: um aviso curto no sino e um comunicado completo">
          <div className="flex flex-col gap-3">
            <div>
              <span className="mb-1 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary">
                <Bell size={12} />
                Aviso
              </span>
              <ItemAviso
                icone={Megaphone}
                texto="Novo comunicado: Retiro de jovens"
                quando="mensagem curta, no sino"
              />
            </div>
            <div>
              <span className="mb-1 inline-flex items-center gap-1 rounded-full bg-info/10 px-2 py-0.5 text-xs font-bold text-info">
                <Megaphone size={12} />
                Comunicado
              </span>
              <div className="rounded-xl border border-border bg-surface p-3">
                <span className="block text-sm font-bold text-text">Retiro de jovens</span>
                <span className="block text-xs text-muted">
                  Mensagem completa, na área Comunicados.
                </span>
              </div>
            </div>
          </div>
        </Moldura>
      );

    case "escala-cartao":
      return (
        <Moldura descricao="Cartão de uma escala: Culto de domingo, aguardando a sua resposta">
          <div className="flex items-center gap-3 rounded-xl border border-border bg-surface p-3">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-success/10 text-center text-[10px] font-bold leading-tight text-success">
              DOM
              <br />
              18
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-bold text-text">Culto de domingo</span>
              <span className="block text-xs text-muted">Louvor · 18:30</span>
              <span className="mt-1 inline-block rounded-full bg-warning/10 px-2 py-0.5 text-xs font-bold text-warning">
                Aguardando sua resposta
              </span>
            </span>
            <ChevronRight size={16} className="text-muted" />
          </div>
        </Moldura>
      );

    case "escala-responder":
      return (
        <Moldura descricao="Faixa de aviso na escala, com o botão Responder">
          <div className="flex items-center gap-3 rounded-xl bg-warning/15 p-3">
            <BellRing size={22} className="shrink-0 text-warning" />
            <span className="min-w-0 flex-1 text-sm font-semibold text-text">
              Aguardando sua resposta
            </span>
            <span
              className={`rounded-lg bg-primary px-3 py-2 text-xs font-bold text-on-primary ${DESTAQUE}`}
            >
              Responder
            </span>
          </div>
        </Moldura>
      );

    case "calendario":
      return (
        <Moldura descricao="Quatro filtros por cor no Calendário: Eventos, Reservas, Biblioteca e Escala">
          <div className="flex flex-wrap gap-2">
            {(
              [
                ["evento", "Eventos"],
                ["reserva", "Reservas"],
                ["biblioteca", "Biblioteca"],
                ["escala", "Escala"],
              ] as const
            ).map(([chave, rotulo]) => (
              <span
                key={chave}
                className="inline-flex items-center gap-2 rounded-full bg-surface-2 px-3 py-1.5 text-xs font-semibold text-text"
              >
                <span className={`h-2 w-2 rounded-full ${CORES_MODULO[chave].ponto}`} />
                {rotulo}
              </span>
            ))}
          </div>
        </Moldura>
      );
  }
}
